<template>
  <div class="supplier-form-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-truck me-2 text-primary"></i>
            <strong>{{ obj.id ? 'Edit' : 'Add' }} Supplier</strong>
          </h4>
          <small class="text-muted">{{ obj.id ? 'Update existing supplier information' : 'Create a new supplier record' }}</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading supplier data...</p>
      </div>
    </div>

    <!-- Supplier Form -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="form-card">
          <CCardHeader class="form-header">
            <div class="d-flex align-items-center">
              <i class="fas fa-building me-2 text-primary"></i>
              <strong>Supplier Information</strong>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Supplier Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Name</strong>
                  <span class="text-danger ms-1">*</span>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="$v.obj.name.$model"
                    :isValid="checkIfValid('name')"
                    :readonly="isReadOnly"
                    invalidFeedback="Supplier name is required (minimum 1 character)"
                    placeholder="Enter supplier name"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Official name of the supplier
                  </small>
                </CCol>
              </CRow>

              <!-- Code -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Code</strong>
                  <span class="text-danger ms-1">*</span>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="$v.obj.code.$model"
                    :isValid="checkIfValid('code')"
                    :readonly="isReadOnly"
                    invalidFeedback="Supplier code is required (minimum 1 character)"
                    placeholder="Enter supplier code"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-hashtag me-1"></i>
                    Unique code for the supplier
                  </small>
                </CCol>
              </CRow>

              <!-- Address -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Address</strong>
                </CCol>
                <CCol sm="9">
                  <CTextarea
                    v-model="obj.address"
                    :readonly="isReadOnly"
                    placeholder="Enter supplier address"
                    rows="3"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-map-marker-alt me-1"></i>
                    Full address of the supplier
                  </small>
                </CCol>
              </CRow>

              <!-- Phone -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Phone</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.phone"
                    :readonly="isReadOnly"
                    placeholder="Enter phone number"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-phone me-1"></i>
                    Main contact number
                  </small>
                </CCol>
              </CRow>

              <!-- Country -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Country</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.country"
                    :readonly="isReadOnly"
                    placeholder="Enter country"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-globe me-1"></i>
                    Country where the supplier is located
                  </small>
                </CCol>
              </CRow>

              <!-- Website -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Website</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.website"
                    :readonly="isReadOnly"
                    placeholder="https://www.example.com"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-link me-1"></i>
                    Official website URL
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Contact Person Section Header -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-user-tie me-2 text-primary"></i>
                    <strong>Contact Person Details</strong>
                  </h6>
                </CCol>
              </CRow>

              <!-- Contact Person Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Contact Person Name</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.contactPersonName"
                    :readonly="isReadOnly"
                    placeholder="Enter contact person name"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-user me-1"></i>
                    Name of the primary contact person
                  </small>
                </CCol>
              </CRow>

              <!-- Contact Person Phone -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Contact Person Phone</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.contactPersonPhone"
                    :readonly="isReadOnly"
                    placeholder="Enter contact person phone"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-mobile-alt me-1"></i>
                    Direct contact number
                  </small>
                </CCol>
              </CRow>

              <!-- Contact Person Email -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Contact Person Email</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.contactPersonEmail"
                    :readonly="isReadOnly"
                    placeholder="contact@example.com"
                    type="email"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-envelope me-1"></i>
                    Email address of the contact person
                  </small>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>

          <!-- Form Footer -->
          <CCardFooter class="form-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <CButton
                  v-if="canSubmit"
                  color="primary"
                  :disabled="!isValid || submitting"
                  @click="onSubmit"
                >
                  <CSpinner v-if="submitting" size="sm" class="me-2" />
                  <i v-else class="fas fa-check-circle me-2"></i>
                  {{ submitting ? 'Saving...' : 'Submit' }}
                </CButton>
                <CButton
                  color="secondary"
                  class="ms-2"
                  @click="cancel"
                  :disabled="submitting"
                >
                  <i class="fas fa-times me-2"></i>
                  Cancel
                </CButton>
              </div>
              <div v-if="!isValid" class="text-danger">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <small>Please fill in all required fields</small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import SupplierApi from "../../lib/supplierApi";
import Helper from "../../lib/helper";
import { required, minLength } from "vuelidate/lib/validators";

const helper = new Helper();

export default {
  name: "Supplier",
  data: () => {
    return {
      infoList: [],
      api: new SupplierApi(),
      obj: {
        name: "",
      },
      submitted: false,
      submitting: false,
      loading: false,
    };
  },
  mounted() {
    // Check view permission first
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }

    // If creating new, check CanAdd
    if (!this.$route.params.id && !this.canAdd) {
      this.$router.push('/admins/SupplierList');
      this.toast('Access Denied', 'You do not have permission to add suppliers', 'danger');
      return;
    }

    this.obj = this.getEmptyObj();
    this.resetObj();
  },
  validations: {
    obj: {
      name: {
        required,
        minLength: minLength(1),
      },
      code: {
        required,
        minLength: minLength(1),
      },
    },
  },

  computed: {
    formString() {
      return JSON.stringify(this.obj, null, 4);
    },
    isValid() {
      return !this.$v.obj.$invalid;
    },
    isDirty() {
      return this.$v.obj.$anyDirty;
    },
    canView() {
      return this.$auth.hasSubModulePermission('SUPPLIERS', 'CanView');
    },
    canAdd() {
      return this.$auth.hasSubModulePermission('SUPPLIERS', 'CanAdd');
    },
    canUpdate() {
      return this.$auth.hasSubModulePermission('SUPPLIERS', 'CanUpdate');
    },
    isReadOnly() {
      if (this.obj.id) {
        return !this.canUpdate;
      } else {
        return !this.canAdd;
      }
    },
    canSubmit() {
      if (this.obj.id) {
        return this.canUpdate;
      } else {
        return this.canAdd;
      }
    },
  },
  methods: {
    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      return !(field.$invalid || field.$model === "");
    },

    onSubmit() {
      this.submitting = true;
      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.toast("Success", "Supplier created successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/SupplierList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then((response) => {
            this.toast("Success", "Supplier updated successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/SupplierList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      }
    },

    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    resetObj() {
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.loading = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
          });
      } else {
        this.obj = this.getEmptyObj();
        this.loading = false;
      }
    },

    getEmptyObj() {
      return {
        name: "",
        address: "",
        country: "",
        phone: "",
        website: "",
        contactPersonName: "",
        contactPersonPhone: "",
        contactPersonEmail: "",
        code: null,
      };
    },

    cancel() {
      this.$router.push({ path: "/admins/SupplierList" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.supplier-form-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form Card */
.form-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.form-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.form-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* Form Body */
.form-card .card-body {
  padding: 2rem 1.5rem;
}

/* Form Labels */
.col-form-label {
  font-weight: 500;
  color: var(--cui-body-color, #495057);
}

.col-form-label .text-danger {
  font-size: 1.2em;
}

/* Form Help Text */
.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Section Header */
.section-header {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--cui-border-color, #e9ecef);
}

/* Divider */
hr {
  border-top: 1px solid var(--cui-border-color, #dee2e6);
}

/* Form Footer */
.form-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .supplier-form-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .form-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .form-footer {
    display: none !important;
  }

  .form-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}
</style>
